import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { devtools } from 'zustand/middleware'

export interface Tenant {
  id: string
  name: string
  slug: string
  domain: string | null
  plan_type: 'FREE' | 'BASIC' | 'PRO' | 'ENTERPRISE'
  is_active: boolean
  is_trial: boolean
  trial_ends_at: string | null
  max_users: number
  max_products: number
  max_storage_mb: number
  created_at: string
  updated_at: string
  onboarding_status?: 'registration' | 'verification' | 'setup' | 'complete'
  onboarding_completed_at?: string | null
  onboarding_progress_percentage?: number
  business_email?: string
  business_phone?: string
  industry?: string
  company_size?: string
}

interface TenantState {
  // State
  tenant: Tenant | null;
  loading: boolean;
  error: string | null;
  isInitialized: boolean;
  
  // Actions
  setTenant: (tenant: Tenant | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setInitialized: (initialized: boolean) => void;
  clearTenant: () => void;
}

export const useTenantStore = create<TenantState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        tenant: null,
        loading: false,
        error: null,
        isInitialized: false,

        // Set tenant action
        setTenant: (tenant: Tenant | null) => {
          set({ tenant, error: null });
        },

        // Set loading action
        setLoading: (loading: boolean) => {
          set({ loading });
        },

        // Set error action
        setError: (error: string | null) => {
          set({ error });
        },

        // Set initialized action
        setInitialized: (isInitialized: boolean) => {
          set({ isInitialized });
        },

        // Clear tenant action
        clearTenant: () => {
          set({ 
            tenant: null, 
            error: null, 
            loading: false 
          });
        },
      }),
      {
        name: 'tenant-storage', // unique name for localStorage key
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({ 
          tenant: state.tenant,
          isInitialized: state.isInitialized
        }), // only persist these fields
      }
    ),
    {
      name: 'tenant-store', // Name for DevTools
      enabled: process.env.NODE_ENV === 'development', // Only enable in development
    }
  )
) 